import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, Trash2, Home, MoreHorizontal, Search, Settings, Eye, EyeOff, Columns
} from "lucide-react";
import { toast } from "sonner";

// @dnd-kit imports
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from '@dnd-kit/sortable';

// Import refactored components
import {
  DEFAULT_DOMAINS,
  DOMAIN_PREFS_KEY,
  VIEW_PREF_KEY,
  getDomainConfig,
  TaskForm,
  FilterPanel,
  CategoryColumn,
  PanoramaCard,
  UrgentSection,
  PrioritySection
} from "@/components/lifetasks";

const LifeTasksPage = () => {
  // Data states
  const [allTasks, setAllTasks] = useState([]);
  const [urgentTasks, setUrgentTasks] = useState([]);
  const [priorityTasks, setPriorityTasks] = useState([]);
  const [customDomains, setCustomDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // VIEW STATE - Panoramica (default) o Bacheca
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem(VIEW_PREF_KEY) || "panoramica";
  });
  
  // UI states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // FILTRI - stati semplici
  const [searchText, setSearchText] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const [showOnlyUrgent, setShowOnlyUrgent] = useState(false);
  const [showOnlyTop3, setShowOnlyTop3] = useState(false);
  const [selectedDomains, setSelectedDomains] = useState([]);
  
  // DRAG AND DROP states
  const [activeDragId, setActiveDragId] = useState(null);
  const dragItem = useRef(null);
  const [isDraggingTask, setIsDraggingTask] = useState(false);
  const [dropTargetCategory, setDropTargetCategory] = useState(null);
  const [dropTargetTaskId, setDropTargetTaskId] = useState(null);
  
  // Domain preferences
  const [domainPrefs, setDomainPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem(DOMAIN_PREFS_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn("Failed to load domain preferences from localStorage:", e);
    }
    const prefs = {};
    Object.keys(DEFAULT_DOMAINS).forEach((key, idx) => {
      prefs[key] = { visible: true, priority: idx };
    });
    return prefs;
  });
  
  // New task form
  const [newTask, setNewTask] = useState({
    title: "", domain: "casa", custom_domain: "", description: "",
    due_date: "", is_urgent: false, is_priority: false, priority_order: null
  });
  const [newCustomDomain, setNewCustomDomain] = useState("");

  // Save preferences
  useEffect(() => {
    localStorage.setItem(DOMAIN_PREFS_KEY, JSON.stringify(domainPrefs));
  }, [domainPrefs]);
  
  useEffect(() => {
    localStorage.setItem(VIEW_PREF_KEY, activeView);
  }, [activeView]);

  // Fetch data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [tasksRes, urgentRes, priorityRes, domainsRes] = await Promise.all([
        api.get('/life-tasks?include_completed=true'),
        api.get('/life-tasks/urgent'),
        api.get('/life-tasks/priorities'),
        api.get('/custom-domains')
      ]);
      setAllTasks(tasksRes.data || []);
      setUrgentTasks(urgentRes.data || []);
      setPriorityTasks(priorityRes.data || []);
      setCustomDomains(domainsRes.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  // ========== FILTRAGGIO ==========
  const filteredTasks = useMemo(() => {
    let result = [...allTasks];
    
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      result = result.filter(t => 
        t.title.toLowerCase().includes(search) ||
        (t.description && t.description.toLowerCase().includes(search))
      );
    }
    
    const hasStateFilters = showOnlyUrgent || showOnlyTop3 || showCompleted;
    
    if (hasStateFilters) {
      result = result.filter(t => {
        if (showOnlyUrgent && t.is_urgent) return true;
        if (showOnlyTop3 && t.is_priority) return true;
        if (showCompleted && t.is_completed) return true;
        return false;
      });
    } else {
      result = result.filter(t => !t.is_completed);
    }
    
    if (selectedDomains.length > 0) {
      result = result.filter(t => selectedDomains.includes(t.domain));
    }
    
    return result;
  }, [allTasks, searchText, showOnlyUrgent, showOnlyTop3, showCompleted, selectedDomains]);
  
  const getTasksForDomain = useCallback((domainKey, customDomainName = "") => {
    return filteredTasks
      .filter(t => {
        if (customDomainName) {
          return t.domain === "altro" && t.custom_domain === customDomainName;
        }
        return t.domain === domainKey && !t.custom_domain;
      })
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [filteredTasks]);
  
  const getAllTasksForDomain = useCallback((domainKey, customDomainName = "") => {
    return allTasks.filter(t => {
      if (customDomainName) {
        return t.domain === "altro" && t.custom_domain === customDomainName;
      }
      return t.domain === domainKey && !t.custom_domain;
    });
  }, [allTasks]);

  // ========== TASK OPERATIONS ==========
  const handleSaveTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) {
      toast.error("Inserisci un titolo");
      return;
    }
    try {
      if (editingTask) {
        await api.put(`/life-tasks/${editingTask.task_id}`, newTask);
        toast.success("Aggiornato!");
      } else {
        await api.post('/life-tasks', newTask);
        toast.success("Creato!");
      }
      resetForm();
      setDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await api.delete(`/life-tasks/${taskId}`);
      toast.success("Eliminato");
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await api.post(`/life-tasks/${taskId}/complete`);
      toast.success("Completato!");
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleTogglePriority = async (task) => {
    try {
      if (task.is_priority) {
        await api.put(`/life-tasks/${task.task_id}`, { ...task, is_priority: false, priority_order: null });
        toast.success("Rimosso dalle priorità");
      } else {
        if (priorityTasks.length >= 3) {
          toast.error("Massimo 3 priorità");
          return;
        }
        await api.put(`/life-tasks/${task.task_id}`, { ...task, is_priority: true });
        toast.success("Aggiunto alle priorità!");
      }
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setNewTask({
      title: task.title, domain: task.domain, custom_domain: task.custom_domain || "",
      description: task.description || "", due_date: task.due_date || "",
      is_urgent: task.is_urgent || false, is_priority: task.is_priority || false,
      priority_order: task.priority_order
    });
    setDialogOpen(true);
  };

  const handleMoveTask = async (taskId, newDomain, customDomain = "") => {
    try {
      await api.post(`/life-tasks/${taskId}/move`, { domain: newDomain, custom_domain: customDomain });
      toast.success("Spostato!");
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const resetForm = () => {
    setNewTask({
      title: "", domain: "casa", custom_domain: "", description: "",
      due_date: "", is_urgent: false, is_priority: false, priority_order: null
    });
    setEditingTask(null);
  };

  const openAddTaskForDomain = (domain, customDomainName = "") => {
    setNewTask({ ...newTask, domain, custom_domain: customDomainName });
    setEditingTask(null);
    setDialogOpen(true);
  };

  // ========== CUSTOM DOMAINS ==========
  const handleAddCustomDomain = async () => {
    if (!newCustomDomain.trim()) return;
    try {
      await api.post('/custom-domains', { name: newCustomDomain.trim() });
      toast.success("Aggiunta!");
      setNewCustomDomain("");
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleDeleteCustomDomain = async (domainId) => {
    try {
      await api.delete(`/custom-domains/${domainId}`);
      toast.success("Eliminata");
      fetchAllData();
    } catch (error) {
      toast.error("Errore");
    }
  };

  const toggleDomainVisibility = (domainKey) => {
    setDomainPrefs(prev => ({
      ...prev,
      [domainKey]: { ...prev[domainKey], visible: !prev[domainKey]?.visible }
    }));
  };

  // ========== DND-KIT SENSORS ==========
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // ========== DND-KIT CATEGORY HANDLERS ==========
  const handleDndDragStart = (event) => setActiveDragId(event.active.id);

  const handleDndDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over || active.id === over.id) return;

    const sortedDomains = getSortedDomains();
    const oldIndex = sortedDomains.findIndex(d => d.key === active.id);
    const newIndex = sortedDomains.findIndex(d => d.key === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedDomains, oldIndex, newIndex);
    const newPrefs = { ...domainPrefs };
    reordered.forEach((domain, idx) => {
      newPrefs[domain.key] = { ...newPrefs[domain.key], priority: idx };
    });
    setDomainPrefs(newPrefs);
    toast.success("Categoria spostata!");
  };

  // ========== CATEGORY MOVEMENT FUNCTIONS ==========
  const moveCategoryToTop = (domainKey) => {
    const sortedDomains = getSortedDomains();
    const currentIndex = sortedDomains.findIndex(d => d.key === domainKey);
    if (currentIndex <= 0) return;
    const reordered = arrayMove(sortedDomains, currentIndex, 0);
    const newPrefs = { ...domainPrefs };
    reordered.forEach((domain, idx) => { newPrefs[domain.key] = { ...newPrefs[domain.key], priority: idx }; });
    setDomainPrefs(newPrefs);
    toast.success("Spostata in cima!");
  };

  const moveCategoryToBottom = (domainKey) => {
    const sortedDomains = getSortedDomains();
    const currentIndex = sortedDomains.findIndex(d => d.key === domainKey);
    if (currentIndex === -1 || currentIndex === sortedDomains.length - 1) return;
    const reordered = arrayMove(sortedDomains, currentIndex, sortedDomains.length - 1);
    const newPrefs = { ...domainPrefs };
    reordered.forEach((domain, idx) => { newPrefs[domain.key] = { ...newPrefs[domain.key], priority: idx }; });
    setDomainPrefs(newPrefs);
    toast.success("Spostata in fondo!");
  };

  const moveCategoryUp = (domainKey) => {
    const sortedDomains = getSortedDomains();
    const currentIndex = sortedDomains.findIndex(d => d.key === domainKey);
    if (currentIndex <= 0) return;
    const reordered = arrayMove(sortedDomains, currentIndex, currentIndex - 1);
    const newPrefs = { ...domainPrefs };
    reordered.forEach((domain, idx) => { newPrefs[domain.key] = { ...newPrefs[domain.key], priority: idx }; });
    setDomainPrefs(newPrefs);
  };

  const moveCategoryDown = (domainKey) => {
    const sortedDomains = getSortedDomains();
    const currentIndex = sortedDomains.findIndex(d => d.key === domainKey);
    if (currentIndex === -1 || currentIndex === sortedDomains.length - 1) return;
    const reordered = arrayMove(sortedDomains, currentIndex, currentIndex + 1);
    const newPrefs = { ...domainPrefs };
    reordered.forEach((domain, idx) => { newPrefs[domain.key] = { ...newPrefs[domain.key], priority: idx }; });
    setDomainPrefs(newPrefs);
  };

  // ========== TASK DRAG AND DROP ==========
  const handleTaskDragStart = (e, task, categoryKey) => {
    dragItem.current = { type: 'task', task, categoryKey };
    setIsDraggingTask(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.task_id);
    setTimeout(() => {
      e.target.style.opacity = '0.5';
      e.target.style.transform = 'scale(0.98)';
    }, 0);
  };

  const handleTaskDragEnd = (e) => {
    if (e?.target) {
      e.target.style.opacity = '1';
      e.target.style.transform = 'scale(1)';
    }
    dragItem.current = null;
    setIsDraggingTask(false);
    setDropTargetCategory(null);
    setDropTargetTaskId(null);
  };

  const handleTaskDragOver = (e, taskId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dragItem.current?.type === 'task') {
      setDropTargetTaskId(taskId);
    }
  };

  const handleTaskDropOnTask = async (e, targetTask, categoryKey, categoryTasks) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!dragItem.current || dragItem.current.type !== 'task') return;
    
    const sourceTask = dragItem.current.task;
    const sourceCategory = dragItem.current.categoryKey;
    
    if (sourceTask.task_id === targetTask.task_id) {
      handleTaskDragEnd();
      return;
    }
    
    if (sourceCategory === categoryKey) {
      const taskIds = categoryTasks.map(t => t.task_id);
      const sourceIndex = taskIds.indexOf(sourceTask.task_id);
      const targetIndex = taskIds.indexOf(targetTask.task_id);
      
      if (sourceIndex !== -1 && targetIndex !== -1) {
        taskIds.splice(sourceIndex, 1);
        taskIds.splice(targetIndex, 0, sourceTask.task_id);
        
        try {
          await api.post('/life-tasks/reorder', { task_ids: taskIds });
          toast.success("Riordinato!");
          fetchAllData();
        } catch (error) {
          toast.error("Errore nel riordinamento");
        }
      }
    } else {
      const isCustomTarget = categoryKey.startsWith('custom_');
      const targetDomain = isCustomTarget ? 'altro' : categoryKey;
      const allDomains = getSortedDomains();
      const customName = isCustomTarget ? allDomains.find(d => d.key === categoryKey)?.name || '' : '';
      
      try {
        await api.post(`/life-tasks/${sourceTask.task_id}/move`, { domain: targetDomain, custom_domain: customName });
        toast.success("Spostato!");
        fetchAllData();
      } catch (error) {
        toast.error("Errore nello spostamento");
      }
    }
    
    handleTaskDragEnd();
  };

  const handleCategoryAreaDragOver = (e, categoryKey) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dragItem.current?.type === 'task') {
      setDropTargetCategory(categoryKey);
    }
  };

  const handleCategoryAreaDrop = async (e, categoryKey) => {
    e.preventDefault();
    
    if (!dragItem.current || dragItem.current.type !== 'task') return;
    
    const sourceTask = dragItem.current.task;
    const sourceCategory = dragItem.current.categoryKey;
    
    if (sourceCategory === categoryKey) {
      handleTaskDragEnd();
      return;
    }
    
    const isCustomTarget = categoryKey.startsWith('custom_');
    const targetDomain = isCustomTarget ? 'altro' : categoryKey;
    const allDomains = getSortedDomains();
    const customName = isCustomTarget ? allDomains.find(d => d.key === categoryKey)?.name || '' : '';
    
    try {
      await api.post(`/life-tasks/${sourceTask.task_id}/move`, { domain: targetDomain, custom_domain: customName });
      toast.success("Spostato!");
      fetchAllData();
    } catch (error) {
      toast.error("Errore nello spostamento");
    }
    
    handleTaskDragEnd();
  };

  // ========== HELPERS ==========
  const getSortedDomains = () => {
    const allDomains = [
      ...Object.keys(DEFAULT_DOMAINS).map(key => ({ key, type: 'default', ...DEFAULT_DOMAINS[key] })),
      ...customDomains.map(cd => ({ 
        key: `custom_${cd.domain_id}`, type: 'custom', domain_id: cd.domain_id,
        name: cd.name, label: cd.name, icon: MoreHorizontal, 
        emoji: "📌", color: "bg-indigo-50 text-indigo-700 border-indigo-200",
        gradient: "from-indigo-50 to-indigo-100", progressColor: "bg-indigo-400"
      }))
    ];
    return allDomains.sort((a, b) => (domainPrefs[a.key]?.priority ?? 999) - (domainPrefs[b.key]?.priority ?? 999));
  };

  const visibleDomains = getSortedDomains().filter(d => domainPrefs[d.key]?.visible !== false);

  const resetFilters = () => {
    setSearchText("");
    setShowCompleted(false);
    setShowOnlyUrgent(false);
    setShowOnlyTop3(false);
    setSelectedDomains([]);
  };

  const activeFiltersCount = [showCompleted, showOnlyUrgent, showOnlyTop3, selectedDomains.length > 0].filter(Boolean).length;
  
  const toggleDomainFilter = (domainKey) => {
    setSelectedDomains(prev => prev.includes(domainKey) ? prev.filter(d => d !== domainKey) : [...prev, domainKey]);
  };
  
  const categoryHasMatchingTasks = (domain) => {
    const isCustom = domain.type === 'custom';
    const domainTasks = isCustom ? getTasksForDomain("altro", domain.name) : getTasksForDomain(domain.key);
    return domainTasks.length > 0;
  };
  
  const displayedDomains = useMemo(() => {
    let domains = visibleDomains;
    
    if (selectedDomains.length > 0) {
      domains = domains.filter(d => {
        if (d.type === 'default') return selectedDomains.includes(d.key);
        return selectedDomains.includes(d.key);
      });
    }
    
    if (showOnlyUrgent || showOnlyTop3 || showCompleted) {
      domains = domains.filter(categoryHasMatchingTasks);
    }
    
    return domains;
  }, [visibleDomains, selectedDomains, showOnlyUrgent, showOnlyTop3, showCompleted, categoryHasMatchingTasks]);
  
  const getGridClass = () => {
    const count = displayedDomains.length;
    if (count === 1) return "grid-cols-1";
    if (count === 2) return "grid-cols-1 sm:grid-cols-2";
    if (count === 3) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
    return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
  };

  // ========== LOADING ==========
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto" data-testid="life-tasks-page">
      
      {/* Header con View Toggle */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Your Life Map</h1>
        
        <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
          <button
            onClick={() => setActiveView("panoramica")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeView === "panoramica" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Home className="w-4 h-4" /> Panoramica
          </button>
          <button
            onClick={() => setActiveView("bacheca")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeView === "bacheca" ? "bg-white shadow-sm text-gray-800" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Columns className="w-4 h-4" /> Bacheca
          </button>
        </div>
      </div>

      {/* Search + Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Cerca..."
            className="pl-9 rounded-full border-gray-200 bg-white"
          />
        </div>
        
        <FilterPanel
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          showCompleted={showCompleted}
          setShowCompleted={setShowCompleted}
          showOnlyUrgent={showOnlyUrgent}
          setShowOnlyUrgent={setShowOnlyUrgent}
          showOnlyTop3={showOnlyTop3}
          setShowOnlyTop3={setShowOnlyTop3}
          selectedDomains={selectedDomains}
          toggleDomainFilter={toggleDomainFilter}
          setSelectedDomains={setSelectedDomains}
          resetFilters={resetFilters}
          activeFiltersCount={activeFiltersCount}
          customDomains={customDomains}
        />
        
        {/* Settings + Add Button */}
        <div className="flex gap-2 ml-auto">
          <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full gap-1.5 h-9 border-gray-300"
                data-testid="tasks-settings-btn"
                title="Mostra/nascondi le aree di vita che ti interessano"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">Categorie</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Personalizza Categorie</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <p className="text-xs text-muted-foreground">
                  Tocca l'occhio per <strong>mostrare</strong> o <strong>nascondere</strong> un'area di vita.
                  Le aree nascoste non compariranno più tra i Compiti.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={newCustomDomain}
                    onChange={(e) => setNewCustomDomain(e.target.value)}
                    placeholder="Nuova categoria..."
                    className="rounded-xl flex-1"
                  />
                  <Button onClick={handleAddCustomDomain} size="sm" className="rounded-xl">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-1">
                  {getSortedDomains().map(domain => {
                    const isVisible = domainPrefs[domain.key]?.visible !== false;
                    const isCustom = domain.type === 'custom';
                    return (
                      <div key={domain.key} className="flex items-center gap-3 p-3 rounded-xl border bg-white">
                        <span className="text-lg">{domain.emoji}</span>
                        <span className="text-sm font-medium flex-1">{domain.label}</span>
                        {isCustom && <Badge variant="outline" className="text-[10px]">Custom</Badge>}
                        <button
                          onClick={() => toggleDomainVisibility(domain.key)}
                          className={`p-1.5 rounded-lg ${isVisible ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-gray-400 bg-gray-50 hover:bg-gray-100'} transition-colors`}
                          title={isVisible ? "Visibile - tocca per nascondere" : "Nascosto - tocca per mostrare"}
                          data-testid={`toggle-domain-${domain.key}`}
                        >
                          {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        {isCustom && (
                          <button onClick={() => handleDeleteCustomDomain(domain.domain_id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <TaskForm
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            editingTask={editingTask}
            newTask={newTask}
            setNewTask={setNewTask}
            onSubmit={handleSaveTask}
            onReset={resetForm}
            customDomains={customDomains}
            priorityTasksCount={priorityTasks.length}
          />
        </div>
      </div>

      {/* ========== PANORAMICA VIEW ========== */}
      {activeView === "panoramica" && (
        <div className="space-y-6">
          <UrgentSection 
            tasks={urgentTasks} 
            onComplete={handleCompleteTask}
            onViewAll={() => setActiveView("bacheca")}
          />
          
          <PrioritySection 
            tasks={priorityTasks} 
            onComplete={handleCompleteTask}
          />
          
          {/* Grid Panoramica */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {displayedDomains.map(domain => {
              const isCustom = domain.type === 'custom';
              return (
                <PanoramaCard
                  key={domain.key}
                  domain={domain}
                  allDomainTasks={isCustom ? getAllTasksForDomain("altro", domain.name) : getAllTasksForDomain(domain.key)}
                  domainTasks={isCustom ? getTasksForDomain("altro", domain.name) : getTasksForDomain(domain.key)}
                  onAddTask={openAddTaskForDomain}
                  onComplete={handleCompleteTask}
                  onViewInBacheca={() => {
                    setSelectedDomains([isCustom ? 'altro' : domain.key]);
                    setActiveView("bacheca");
                  }}
                />
              );
            })}
          </div>
          
          {displayedDomains.length === 0 && (
            <div className="text-center py-16">
              <p className="text-5xl mb-4">🔍</p>
              <p className="text-gray-500 mb-2">Nessuna categoria corrisponde ai filtri</p>
              <Button onClick={resetFilters} variant="outline" className="rounded-xl">
                Mostra tutte le categorie
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ========== BACHECA VIEW (Kanban) ========== */}
      {activeView === "bacheca" && (
        <div className="space-y-6">
          <UrgentSection 
            tasks={urgentTasks} 
            onComplete={handleCompleteTask}
            onEdit={handleEditTask}
            showViewAll={true}
          />
          
          <PrioritySection 
            tasks={priorityTasks} 
            onComplete={handleCompleteTask}
          />
          
          {/* Kanban Grid con @dnd-kit */}
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDndDragStart}
            onDragEnd={handleDndDragEnd}
          >
            <SortableContext items={displayedDomains.map(d => d.key)} strategy={rectSortingStrategy}>
              <div className={`grid ${getGridClass()} gap-4`}>
                {displayedDomains.map(domain => {
                  const isCustom = domain.type === 'custom';
                  const domainTasks = isCustom ? getTasksForDomain("altro", domain.name) : getTasksForDomain(domain.key);
                  
                  return (
                    <CategoryColumn
                      key={domain.key}
                      domain={domain}
                      domainTasks={domainTasks}
                      activeDragId={activeDragId}
                      isDraggingTask={isDraggingTask}
                      dropTargetCategory={dropTargetCategory}
                      dropTargetTaskId={dropTargetTaskId}
                      dragItem={dragItem}
                      onAddTask={openAddTaskForDomain}
                      onCompleteTask={handleCompleteTask}
                      onEditTask={handleEditTask}
                      onDeleteTask={handleDeleteTask}
                      onTogglePriority={handleTogglePriority}
                      onMoveTask={handleMoveTask}
                      onMoveCategoryToTop={moveCategoryToTop}
                      onMoveCategoryUp={moveCategoryUp}
                      onMoveCategoryDown={moveCategoryDown}
                      onMoveCategoryToBottom={moveCategoryToBottom}
                      onTaskDragStart={handleTaskDragStart}
                      onTaskDragEnd={handleTaskDragEnd}
                      onTaskDragOver={handleTaskDragOver}
                      onTaskDropOnTask={handleTaskDropOnTask}
                      onCategoryAreaDragOver={handleCategoryAreaDragOver}
                      onCategoryAreaDrop={handleCategoryAreaDrop}
                      setDropTargetCategory={setDropTargetCategory}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
          
          {displayedDomains.length === 0 && (
            <div className="text-center py-16">
              <p className="text-5xl mb-4">🔍</p>
              <p className="text-gray-500 mb-2">Nessuna categoria corrisponde ai filtri</p>
              <Button onClick={resetFilters} variant="outline" className="rounded-xl">
                Mostra tutte le categorie
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LifeTasksPage;
